import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import EvidenceUpload from './EvidenceUpload';

const ClaimFilingWizard = ({ onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    disputeType: '',
    trailerName: '',
    trailerImage: "https://images.unsplash.com/photo-1642098043424-5eb2f9f1135d",
    trailerImageAlt: 'Trailer image',
    opposingParty: '',
    opposingPartyRole: 'owner',
    title: '',
    description: '',
    incidentDate: '',
    amount: '',
    evidence: []
  });

  const [errors, setErrors] = useState({});

  const disputeTypes = [
  { value: 'damage', label: 'Damage Claim', description: 'Report trailer damage or condition issues' },
  { value: 'no-show', label: 'No-Show', description: 'Renter or owner did not appear for scheduled pickup/return' },
  { value: 'policy_violation', label: 'Policy Violation', description: 'Unauthorized use or terms violation' },
  { value: 'payment', label: 'Payment Dispute', description: 'Billing, refund, or fee disputes' },
  { value: 'other', label: 'Other', description: 'Other dispute types' }];


  const steps = [
  { number: 1, title: 'Dispute Type', icon: 'FileText' },
  { number: 2, title: 'Details', icon: 'Edit' },
  { number: 3, title: 'Evidence', icon: 'Upload' },
  { number: 4, title: 'Review', icon: 'CheckCircle' }];


  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.disputeType) newErrors.disputeType = 'Please select a dispute type';
      if (!formData?.trailerName) newErrors.trailerName = 'Trailer name is required';
      if (!formData?.opposingParty) newErrors.opposingParty = 'Opposing party name is required';
    }

    if (step === 2) {
      if (!formData?.title) newErrors.title = 'Title is required';
      if (!formData?.description) newErrors.description = 'Description is required';
      if (formData?.description?.length < 50) newErrors.description = 'Description must be at least 50 characters';
      if (!formData?.incidentDate) newErrors.incidentDate = 'Incident date is required';
      if (!formData?.amount) newErrors.amount = 'Amount is required';
    }

    if (step === 3) {
      if (formData?.evidence?.length === 0) newErrors.evidence = 'Please upload at least one piece of evidence';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = () => {
    if (validateStep(3)) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold text-foreground">
              File New Claim
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-smooth">
              
              <Icon name="X" size={24} strokeWidth={2} />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps?.map((step, index) =>
            <React.Fragment key={step?.number}>
                <div className="flex flex-col items-center">
                  <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-smooth ${
                  currentStep >= step?.number ?
                  'bg-primary text-primary-foreground' :
                  'bg-muted text-muted-foreground'}`
                  }>
                  
                    {currentStep > step?.number ?
                  <Icon name="Check" size={20} strokeWidth={2} /> :

                  <Icon name={step?.icon} size={20} strokeWidth={2} />
                  }
                  </div>
                  <span className="font-caption text-xs text-muted-foreground mt-2 hidden sm:block">
                    {step?.title}
                  </span>
                </div>
                {index < steps?.length - 1 &&
              <div
                className={`flex-1 h-1 mx-2 rounded transition-smooth ${
                currentStep > step?.number ? 'bg-primary' : 'bg-muted'}`
                } />

              }
              </React.Fragment>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Dispute Type */}
          {currentStep === 1 &&
          <div className="space-y-6">
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
                  Select Dispute Type
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {disputeTypes?.map((type) =>
                <button
                  key={type?.value}
                  onClick={() => handleInputChange('disputeType', type?.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-smooth ${
                  formData?.disputeType === type?.value ?
                  'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`
                  }>
                  
                      <h4 className="font-body font-semibold text-foreground mb-1">
                        {type?.label}
                      </h4>
                      <p className="font-caption text-sm text-muted-foreground">
                        {type?.description}
                      </p>
                    </button>
                )}
                </div>
                {errors?.disputeType &&
              <p className="font-caption text-sm text-error mt-2">{errors?.disputeType}</p>
              }
              </div>

              <Input
              label="Trailer Name"
              placeholder="Enter trailer name"
              value={formData?.trailerName}
              onChange={(e) => handleInputChange('trailerName', e?.target?.value)}
              error={errors?.trailerName}
              required />
            

              <div>
                <label className="block font-body text-sm font-medium text-foreground mb-2">
                  Opposing Party Role <span className="text-error">*</span>
                </label>
                <div className="flex gap-4">
                  <button
                  onClick={() => handleInputChange('opposingPartyRole', 'owner')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-smooth ${
                  formData?.opposingPartyRole === 'owner' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`
                  }>
                  
                    <Icon name="User" size={24} className="mx-auto mb-2" strokeWidth={2} />
                    <p className="font-body font-semibold text-foreground">Owner</p>
                  </button>
                  <button
                  onClick={() => handleInputChange('opposingPartyRole', 'renter')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-smooth ${
                  formData?.opposingPartyRole === 'renter' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`
                  }>
                  
                    <Icon name="UserCheck" size={24} className="mx-auto mb-2" strokeWidth={2} />
                    <p className="font-body font-semibold text-foreground">Renter</p>
                  </button>
                </div>
              </div>

              <Input
              label="Opposing Party Name"
              placeholder="Enter name"
              value={formData?.opposingParty}
              onChange={(e) => handleInputChange('opposingParty', e?.target?.value)}
              error={errors?.opposingParty}
              required />
            
            </div>
          }

          {/* Step 2: Details */}
          {currentStep === 2 &&
          <div className="space-y-6">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
                Claim Details
              </h3>

              <Input
              label="Claim Title"
              placeholder="Brief summary of the dispute"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              error={errors?.title}
              required />
            

              <div>
                <label className="block font-body text-sm font-medium text-foreground mb-2">
                  Description <span className="text-error">*</span>
                </label>
                <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder="Provide detailed description of the incident (minimum 50 characters)"
                rows={6}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" />
              
                <div className="flex justify-between items-center mt-2">
                  <p className="font-caption text-xs text-muted-foreground">
                    {formData?.description?.length} / 50 minimum characters
                  </p>
                  {errors?.description &&
                <p className="font-caption text-sm text-error">{errors?.description}</p>
                }
                </div>
              </div>

              <Input
              label="Incident Date"
              type="date"
              value={formData?.incidentDate}
              onChange={(e) => handleInputChange('incidentDate', e?.target?.value)}
              error={errors?.incidentDate}
              required />
            

              <Input
              label="Claim Amount ($)"
              type="number"
              placeholder="0.00"
              value={formData?.amount}
              onChange={(e) => handleInputChange('amount', e?.target?.value)}
              error={errors?.amount}
              required />
            
            </div>
          }

          {/* Step 3: Evidence */}
          {currentStep === 3 &&
          <div className="space-y-6">
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  Upload Evidence
                </h3>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Upload photos, videos, or documents to support your claim
                </p>
              </div>

              <EvidenceUpload
              evidence={formData?.evidence}
              onChange={(evidence) => handleInputChange('evidence', evidence)} />
            

              {errors?.evidence &&
            <p className="font-caption text-sm text-error">{errors?.evidence}</p>
            }
            </div>
          }

          {/* Step 4: Review */}
          {currentStep === 4 &&
          <div className="space-y-6">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
                Review Your Claim
              </h3>

              <div className="bg-muted/30 rounded-lg p-6 space-y-4">
                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-1">Dispute Type</p>
                  <p className="font-body font-semibold text-foreground">
                    {disputeTypes?.find((t) => t?.value === formData?.disputeType)?.label}
                  </p>
                </div>

                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-1">Trailer</p>
                  <p className="font-body font-semibold text-foreground">{formData?.trailerName}</p>
                </div>

                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-1">Opposing Party</p>
                  <p className="font-body font-semibold text-foreground">
                    {formData?.opposingParty} ({formData?.opposingPartyRole})
                  </p>
                </div>

                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-1">Title</p>
                  <p className="font-body font-semibold text-foreground">{formData?.title}</p>
                </div>

                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-1">Description</p>
                  <p className="font-body text-sm text-foreground">{formData?.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-caption text-xs text-muted-foreground mb-1">Incident Date</p>
                    <p className="font-body font-semibold text-foreground">{formData?.incidentDate}</p>
                  </div>
                  <div>
                    <p className="font-caption text-xs text-muted-foreground mb-1">Claim Amount</p>
                    <p className="font-body font-semibold text-foreground">${formData?.amount}</p>
                  </div>
                </div>

                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-2">Evidence</p>
                  <p className="font-body text-sm text-foreground">
                    {formData?.evidence?.length} file(s) uploaded
                  </p>
                </div>
              </div>

              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="font-body text-sm font-semibold text-foreground mb-1">
                      Important Notice
                    </p>
                    <p className="font-caption text-xs text-muted-foreground">
                      By submitting this claim, you confirm that all information provided is accurate and truthful. 
                      False claims may result in account suspension.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? onClose : handleBack}>
              
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              onClick={currentStep === 4 ? handleSubmit : handleNext}
              iconName={currentStep === 4 ? 'Check' : 'ArrowRight'}
              iconPosition="right">
              
              {currentStep === 4 ? 'Submit Claim' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>);

};

export default ClaimFilingWizard;